// R-24 (Round 3, Lane 3; AGENTS.md Core flow: "the calendar button matches the calendar they use (Google or Apple), learned
// in onboarding or settings"): which calendar this person uses.
//
// Where it lives, and why both places:
//   - on this phone: localStorage 'mg_cal_app' (the key the itinerary and Settings already use; lib/owner.ts keeps it when
//     the person changes), so the detail can show one button at once, with no wait and no network;
//   - on the profile: profiles.first_run.calendar, through POST /profile {set: {calendar}}, so a new phone or browser after
//     sign-in starts with the same calendar, and delete-me removes it with the profile. No new column, no migration.
// 'mg_cal_synced' remembers which value was last saved for which person (`<user id>:<value>`), so a choice made in
// Settings (which writes only the phone's key) is saved on the next calendar tap, and a sign-in to another account saves
// it there too.
import { callFn, ensureSession, supabase } from './api'

export type CalApp = 'google' | 'ics'
const KEY = 'mg_cal_app'
const SYNCED = 'mg_cal_synced'
const get = (k: string) => { try { return localStorage.getItem(k) } catch { return null } }
const put = (k: string, v: string) => { try { localStorage.setItem(k, v) } catch { /* convenience only */ } }
export const isCalApp = (v: unknown): v is CalApp => v === 'google' || v === 'ics'

/** The calendar they told us they use (on this phone), or null when we have not asked yet. */
export function chosenCalendar(): CalApp | null { const v = get(KEY); return isCalApp(v) ? v : null }

async function uid(): Promise<string | null> {
  try { return (await supabase.auth.getSession()).data.session?.user.id ?? null } catch { return null }
}
let saving: Promise<void> | null = null
/** Save the phone's choice on the profile when it is not saved there yet for this person. Never throws. */
export function saveCalendarChoice(): Promise<void> {
  const v = chosenCalendar()
  if (!v) return Promise.resolve()
  return (saving ??= (async () => {
    try {
      const id = (await uid()) ?? (await ensureSession()).user.id
      if (get(SYNCED) === `${id}:${v}`) return
      await callFn('profile', { set: { calendar: v } })
      put(SYNCED, `${id}:${v}`)
    } catch { /* kept on this phone; saved on the next tap */ }
  })().finally(() => { saving = null }))
}

/** They picked a calendar (the detail's question, its "instead" link, the itinerary): remember it here and on the profile. */
export function chooseCalendar(v: CalApp): void {
  put(KEY, v)
  void saveCalendarChoice()
}

/**
 * The profile's answer arrived (lib/v2 getFirstRun / setFirstRun): a phone with no choice takes the profile's (a new phone
 * after sign-in); a choice made on this phone since the last save is saved (this phone wins); otherwise a different saved
 * choice (changed on another phone) is taken.
 */
export async function adoptCalendar(server: unknown): Promise<void> {
  const local = chosenCalendar()
  const id = await uid()
  if (!id) return
  if (!local) { if (isCalApp(server)) { put(KEY, server); put(SYNCED, `${id}:${server}`) } return }
  if (get(SYNCED) !== `${id}:${local}`) { void saveCalendarChoice(); return } // picked here (or in Settings) since the last save
  if (isCalApp(server) && server !== local) { put(KEY, server); put(SYNCED, `${id}:${server}`) } // changed on another phone
  else if (!isCalApp(server)) { try { localStorage.removeItem(SYNCED) } catch { /* next tap */ } void saveCalendarChoice() } // the profile lost it (a sign-in merge kept the account's first run)
}
